// SPDX-FileCopyrightText: (C) 2023 Jason Ish <jason@codemonkey.net>
// SPDX-License-Identifier: MIT

import { TIME_RANGE, Top } from "../Top";
import { createEffect, createSignal, onCleanup } from "solid-js";
import { API, fetchAgg } from "../api";
import { RefreshButton } from "../common/RefreshButton";
import { Chart, ChartConfiguration } from "chart.js";
import { useSearchParams } from "@solidjs/router";
import { SensorSelect } from "../common/SensorSelect";
import { loadingTracker } from "../util";
import { CountValueDataTable } from "../components/CountValueDataTable";
import { StatCard } from "../components/StatCard";
import { Colors } from "../common/colors";
import { createStore } from "solid-js/store";
import type { SetStoreFunction } from "solid-js/store";
import dayjs from "dayjs";

interface CountValueRow {
  count: number;
  key: any;
}

interface Model {
  rows: CountValueRow[];
  loading: boolean;
  timestamp: null | dayjs.Dayjs;
}

function defaultModel(): Model {
  return {
    rows: [],
    loading: false,
    timestamp: null,
  };
}

interface Stats {
  alerts: number | null;
  high: number | null;
  medium: number | null;
  low: number | null;
  signatures: number | null;
  signaturesCapped: boolean;
}

function defaultStats(): Stats {
  return {
    alerts: null,
    high: null,
    medium: null,
    low: null,
    signatures: null,
    signaturesCapped: false,
  };
}

// The distinct signature count is exact until this aggregation row
// limit is hit, then shown as a lower bound.
const SIGNATURE_LIMIT = 500;

export function AlertsDashboard() {
  const [version, setVersion] = createSignal(0);

  const [loading, setLoading] = createSignal(0);

  const [searchParams, setSearchParams] = useSearchParams<{
    sensor?: string;
    q?: string;
  }>();

  const [mostAlerting, setMostAlerting] = createStore<Model>(defaultModel());

  const [leastAlerting, setLeastAlerting] = createStore<Model>(defaultModel());

  const [mostAlertingSource, setMostAlertingSource] =
    createStore<Model>(defaultModel());

  const [leastAlertingSource, setLeastAlertingSource] =
    createStore<Model>(defaultModel());

  const [mostAlertingDest, setMostAlertingDest] =
    createStore<Model>(defaultModel());

  const [leastAlertingDest, setLeastAlertingDest] =
    createStore<Model>(defaultModel());

  const [categories, setCategories] = createStore<Model>(defaultModel());

  const [appProtos, setAppProtos] = createStore<Model>(defaultModel());

  const [stats, setStats] = createStore<Stats>(defaultStats());

  let histogram: any = undefined;

  onCleanup(() => {
    API.cancelAllSse();
  });

  createEffect(() => {
    refresh();
  });

  function buildChart(response: any) {
    const dataValues: number[] = [];
    const dataLabels: number[] = [];
    response.data.forEach((e: any) => {
      dataValues.push(e.count);
      dataLabels.push(e.time);
    });

    const ctx = (
      document.getElementById("histogram") as HTMLCanvasElement
    ).getContext("2d") as CanvasRenderingContext2D;

    const config: ChartConfiguration = {
      type: "bar",
      data: {
        labels: dataLabels,
        datasets: [
          {
            data: dataValues,
            backgroundColor: Colors[0],
            borderColor: Colors[0],
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: "Alerts Over Time",
            padding: 0,
          },
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            type: "time",
            ticks: {
              source: "auto",
            },
          },
        },
      },
    };
    if (histogram) {
      histogram.destroy();
    }
    histogram = new Chart(ctx, config);
  }

  function refresh() {
    console.log("Alerts.refresh");
    setVersion((version) => version + 1);
    const timeRange = TIME_RANGE();

    let queryString = [];
    if (searchParams.q) {
      queryString.push(searchParams.q);
    }
    if (searchParams.sensor) {
      queryString.push(`host:${searchParams.sensor}`);
    }

    API.histogramTime({
      time_range: timeRange,
      event_type: "alert",
      query_string: queryString.length > 0 ? queryString.join(" ") : undefined,
    }).then((response) => {
      buildChart(response);
    });

    // Stat cards: alert counts by severity and distinct signatures.
    setStats(defaultStats());
    loadingTracker(setLoading, async () => {
      const request = {
        order: "desc" as const,
        time_range: timeRange,
      };
      const [severities, signatures] = await Promise.all([
        fetchAgg({
          ...request,
          size: 100,
          field: "alert.severity",
          q: [...queryString, "event_type:alert"].join(" "),
        }),
        fetchAgg({
          ...request,
          size: SIGNATURE_LIMIT,
          field: "alert.signature",
          q: [...queryString, "event_type:alert"].join(" "),
        }),
      ]);
      const severityCount = (severity: string) =>
        severities.rows.find((row) => String(row.key) === severity)?.count || 0;
      setStats({
        alerts: severities.rows.reduce((acc, row) => acc + row.count, 0),
        high: severityCount("1"),
        medium: severityCount("2"),
        low: severityCount("3"),
        signatures: signatures.rows.length,
        signaturesCapped: signatures.rows.length >= SIGNATURE_LIMIT,
      });
    });

    let loaders: {
      field: string;
      q: string;
      order: "desc" | "asc";
      setter: SetStoreFunction<Model>;
    }[] = [
      // Top alerting signatures.
      {
        field: "alert.signature",
        q: "event_type:alert",
        order: "desc",
        setter: setMostAlerting,
      },
      // Least alerting signatures.
      {
        field: "alert.signature",
        q: "event_type:alert",
        order: "asc",
        setter: setLeastAlerting,
      },
      // Top alerting source addresses.
      {
        field: "src_ip",
        q: "event_type:alert",
        order: "desc",
        setter: setMostAlertingSource,
      },
      // Least alerting source addresses.
      {
        field: "src_ip",
        q: "event_type:alert",
        order: "asc",
        setter: setLeastAlertingSource,
      },
      // Top alerting destination addresses.
      {
        field: "dest_ip",
        q: "event_type:alert",
        order: "desc",
        setter: setMostAlertingDest,
      },
      // Least alerting destination addresses.
      {
        field: "dest_ip",
        q: "event_type:alert",
        order: "asc",
        setter: setLeastAlertingDest,
      },
      // Alert categories.
      {
        field: "alert.category",
        q: "event_type:alert",
        order: "desc",
        setter: setCategories,
      },
      // Application protocols of alerting traffic.
      {
        field: "app_proto",
        q: "event_type:alert",
        order: "desc",
        setter: setAppProtos,
      },
    ];

    for (const loader of loaders) {
      if (loader.setter) {
        loader.setter("loading", true);
      }

      let q = [...queryString];
      if (loader.q) {
        q.push(loader.q);
      }

      let request = {
        time_range: timeRange,
        field: loader.field,
        order: loader.order,
        q: q.length > 0 ? q.join(" ") : undefined,
      };

      loadingTracker(setLoading, () => {
        return API.getSseAgg(request, version, (data: any) => {
          if (data) {
            loader.setter("rows", data.rows);
            loader.setter("timestamp", dayjs(data.earliest_ts));
          }
        });
      }).finally(() => {
        if (loader.setter) {
          loader.setter("loading", false);
        }
      });
    }
  }

  const formatSuffix = (timestamp: dayjs.Dayjs | null) => {
    if (timestamp) {
      return `since ${timestamp.fromNow()}`;
    }
    return undefined;
  };

  const formatCount = (count: number | null) =>
    count === null ? null : count.toLocaleString();

  const percentOfAlerts = (count: number | null) => {
    if (count === null || !stats.alerts) {
      return undefined;
    }
    return `${((count / stats.alerts) * 100).toFixed(1)}% of alerts`;
  };

  return (
    <>
      <Top />

      <div class="container-fluid">
        <div class="row mt-2">
          <div class="col">
            <form class="d-flex flex-wrap align-items-center gap-2">
              <div>
                <RefreshButton loading={loading()} refresh={refresh} />
              </div>
              <div class="d-inline-flex">
                <SensorSelect
                  selected={searchParams.sensor}
                  onchange={(sensor) => {
                    setSearchParams({ sensor: sensor });
                  }}
                />
              </div>
            </form>
          </div>

          <div class="col">
            <form
              class="input-group"
              onsubmit={(e) => {
                e.preventDefault();
                // blurInputs();
                // applyFilter(e.currentTarget.filter.value);
                setSearchParams({ q: e.currentTarget.filter.value });
              }}
            >
              <input
                id="filter-input"
                type="text"
                class="form-control"
                name="filter"
                placeholder="Search..."
                value={searchParams.q || ""}
                onkeydown={(e) => {
                  if (
                    e.code === "Escape" ||
                    e.key === "Escape" ||
                    e.keyCode === 27
                  ) {
                    // blurInputs();
                  }
                  e.stopPropagation();
                }}
              />
              <button class="btn btn-secondary" type="submit">
                Apply
              </button>
              <button
                class="btn btn-secondary"
                type="button"
                onclick={() => {
                  setSearchParams({ q: undefined });
                }}
              >
                Clear
              </button>
            </form>
          </div>
        </div>

        <div class="row mt-2 g-2">
          <div class="col-6 col-lg">
            <StatCard value={formatCount(stats.alerts)} label="Alerts" />
          </div>
          <div class="col-6 col-lg">
            <StatCard
              value={formatCount(stats.high)}
              label="High (1)"
              sub={percentOfAlerts(stats.high)}
            />
          </div>
          <div class="col-6 col-lg">
            <StatCard
              value={formatCount(stats.medium)}
              label="Medium (2)"
              sub={percentOfAlerts(stats.medium)}
            />
          </div>
          <div class="col-6 col-lg">
            <StatCard
              value={formatCount(stats.low)}
              label="Low (3)"
              sub={percentOfAlerts(stats.low)}
            />
          </div>
          <div class="col-6 col-lg">
            <StatCard
              value={
                stats.signaturesCapped
                  ? `${stats.signatures}+`
                  : formatCount(stats.signatures)
              }
              label="Signatures"
            />
          </div>
        </div>

        <div class="row">
          <div class="col mt-2">
            <canvas id="histogram" class="app-chart-alerts"></canvas>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col">
            <CountValueDataTable
              title={"Most Alerting Signatures"}
              label={"Signature"}
              searchField="alert.signature"
              rows={mostAlerting.rows}
              loading={mostAlerting.loading}
              suffix={formatSuffix(mostAlerting.timestamp)}
            />
          </div>

          <div class="col">
            <CountValueDataTable
              title={"Least Alerting Signatures"}
              label={"Signature"}
              searchField="alert.signature"
              rows={leastAlerting.rows}
              loading={leastAlerting.loading}
              suffix={formatSuffix(leastAlerting.timestamp)}
            />
          </div>
        </div>

        <div class="row">
          <div class="col mt-2">
            <CountValueDataTable
              title={"Alert Categories"}
              label={"Category"}
              searchField="alert.category"
              rows={categories.rows}
              loading={categories.loading}
              suffix={formatSuffix(categories.timestamp)}
            />
          </div>
          <div class="col mt-2">
            <CountValueDataTable
              title={"Alerting Protocols"}
              label={"Protocol"}
              searchField="app_proto"
              tooltip={
                "Application protocol of the alerting traffic. A value of failed means the protocol could not be identified."
              }
              rows={appProtos.rows}
              loading={appProtos.loading}
              suffix={formatSuffix(appProtos.timestamp)}
            />
          </div>
        </div>

        <div class="row">
          <div class="col mt-2">
            <CountValueDataTable
              title={"Most Alerting Source Addresses"}
              label={"Address"}
              searchField={"@ip"}
              rows={mostAlertingSource.rows}
              loading={mostAlertingSource.loading}
              suffix={formatSuffix(mostAlertingSource.timestamp)}
            />
          </div>
          <div class="col mt-2">
            <CountValueDataTable
              title={"Least Alerting Source Addresses"}
              label={"Address"}
              searchField={"@ip"}
              rows={leastAlertingSource.rows}
              loading={leastAlertingSource.loading}
              suffix={formatSuffix(leastAlertingSource.timestamp)}
            />
          </div>
        </div>

        <div class="row">
          <div class="col mt-2">
            <CountValueDataTable
              title={"Most Alerting Destination Addresses"}
              label={"Address"}
              searchField={"@ip"}
              rows={mostAlertingDest.rows}
              loading={mostAlertingDest.loading}
              suffix={formatSuffix(mostAlertingDest.timestamp)}
            />
          </div>
          <div class="col mt-2">
            <CountValueDataTable
              title={"Least Alerting Destination Addresses"}
              label={"Address"}
              searchField={"@ip"}
              rows={leastAlertingDest.rows}
              loading={leastAlertingDest.loading}
              suffix={formatSuffix(leastAlertingDest.timestamp)}
            />
          </div>
        </div>
      </div>
    </>
  );
}
